import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChatMessagesList from './ChatMessagesList';

interface ChatThread {
  id: string;
  user_name: string;
  title: string;
  last_updated: string;
  messages: ChatMessage[];
  isExpanded: boolean;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  content: string;
  sender: string;
  app_id: string;
}

interface ChatHistoryTableProps {
  isLoading: boolean;
  chatThreads: ChatThread[];
  loadingThreadId: string | null;
  setChatThreads: React.Dispatch<React.SetStateAction<ChatThread[]>>;
  getMessages: (threadId: string) => Promise<void>;
  handleDeleteThread: (threadId: string) => void;
  safeAiIconSrc: string;
  assistant: any;
}

const ChatHistoryTable: React.FC<ChatHistoryTableProps> = ({
  isLoading,
  chatThreads,
  loadingThreadId,
  setChatThreads,
  getMessages,
  handleDeleteThread,
  safeAiIconSrc,
  assistant,
}) => {
  const toggleThreadExpansion = (threadId: string) => {
    setChatThreads(threads => 
      threads.map(thread => 
        thread.id === threadId 
          ? { ...thread, isExpanded: !thread.isExpanded } 
          : thread
      )
    );
    const thread = chatThreads.find((t) => t.id === threadId);
    if (thread && !thread.isExpanded) {
      getMessages(threadId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="flex justify-center items-center h-64">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  return (
    <table className="min-w-full table-fixed divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="w-[10%] px-4 py-2 text-left text-black font-semibold text-sm">Name</th>
          <th className="w-[10%] px-4 py-2 text-left text-black font-semibold text-sm">Topic</th>
          <th className="w-[16%] px-4 py-2 text-left text-black font-semibold text-sm">Date/Time</th>
          <th className="w-[50%] px-4 py-2 text-left text-black font-semibold text-sm">Chat</th>
          <th className="w-[9%] px-4 py-2 text-center text-black font-semibold text-sm">Action</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {chatThreads.length > 0 ? (
          chatThreads.map((thread) => (
            <tr key={thread.id} className="align-top">
              <td className="w-[6%] px-4 py-2 text-left">
                <div className="text-sm break-words">
                  {thread?.user_name ? thread?.user_name : 'Anonymous'}
                </div>
              </td>
              <td className="w-[15%] px-4 py-2">
                <div className="text-sm text-left">{thread.title}</div>
              </td>
              <td className="w-[17%] px-4 py-2">
                <div className="text-sm text-left">
                  {new Date(thread.last_updated).toLocaleString("en-US", { timeZone: "America/Chicago" })}
                </div>
              </td>
              <td className="w-[48%] px-4 py-2">
                <div className="flex flex-col space-y-2 w-full">
                  <div className="flex items-center mb-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleThreadExpansion(thread.id);
                      }}
                      className="text-gray-600 hover:text-gray-900 mr-2"
                    >
                      {thread.isExpanded ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </button>
                    <span className="text-sm">Chat messages</span>
                  </div>

                  {thread.isExpanded && (
                    <ChatMessagesList
                      thread={thread}
                      loadingThreadId={loadingThreadId}
                      safeAiIconSrc={safeAiIconSrc}
                      assistant={assistant}
                    />
                  )}
                </div>
              </td>
              <td className="w-[9%] px-4 py-2">
                <div className="flex items-center h-full">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteThread(thread.id);
                    }}
                    className="border border-gray-400 bg-white text-black px-3 py-1 rounded hover:bg-black hover:text-white transition-colors duration-200"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={5} className="px-4 py-4 text-center text-gray-500">
              No records found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default ChatHistoryTable;